import React, { useState, useEffect } from 'react';
import { API_PATHS } from '../../../utils/constants';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Chip,
  Alert,
  Button,
  Stack
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import MockDataIndicator from '../../../Components/Common/MockDataIndicator';

const AccountSummary = () => {
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMockData, setIsMockData] = useState(false);

  useEffect(() => {
    fetchAccountSummary();
  }, []);

  const fetchAccountSummary = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get auth token from localStorage
      const authInfo = localStorage.getItem('authInfo');
      if (!authInfo) {
        setError('Not authenticated. Please log in.');
        setLoading(false);
        return;
      }

      const { token } = JSON.parse(authInfo);
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        setLoading(false);
        return;
      }

      // Make API call to fetch account summary
      const response = await axios.get(`${API_PATHS.V1}/accounts/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;
      // Map 'status' to 'accountStatus' if needed
      if (!data.accountStatus && data.status) {
        data.accountStatus = data.status;
      }
      if (!data.accountStatus) {
        data.accountStatus = 'ACTIVE';
      }
      setAccountData(data);
      setIsMockData(false);
    } catch (err) {
      console.error('Error fetching account summary:', err);
      setError('Failed to load account summary. Using mock data.');

      // API call failed — show zeros instead of fake data
      setAccountData({
        accountNumber: '',
        organizationName: '',
        walletBalance: 0,
        totalInvoiced: 0,
        totalPaid: 0,
        creditLimit: 0,
        paymentTerms: '',
        accountStatus: ''
      });
      setIsMockData(true);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !accountData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={fetchAccountSummary}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "'Roboto', 'Arial', sans-serif",
            fontSize: '0.9rem',
            fontWeight: 500
          }}
        >
          Account Summary
        </Typography>
        <MockDataIndicator isMock={isMockData} position="chip" size="small" />
      </Box>
      
      {/* Main Summary Card */}
      <Card sx={{ mb: 3, boxShadow: 1, borderRadius: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="subtitle2" 
                gutterBottom 
                sx={{ 
                  fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Account Information
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}>Account Number</Typography>
                <Typography variant="body2" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif", fontWeight: 500 }}>{accountData.accountNumber}</Typography>
              </Box>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}>Organization Name</Typography>
                <Typography variant="body2" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif", fontWeight: 500 }}>{accountData.organizationName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}>Account Status</Typography>
                <Chip
                  label={accountData.accountStatus}
                  color={accountData.accountStatus === 'ACTIVE' ? 'success' : 'error'}
                  size="small"
                  sx={{ mt: 0.5, height: '20px', '& .MuiChip-label': { fontSize: '0.75rem', fontFamily: "'Roboto', 'Arial', sans-serif" } }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography 
                variant="subtitle2" 
                gutterBottom 
                sx={{ 
                  fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Payment Information
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}>Payment Terms</Typography>
                <Typography variant="body2" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif", fontWeight: 500 }}>{accountData.paymentTerms || 'N/A'}</Typography>
              </Box>

              {/* Only show Credit Limit for CUSTOMER organizations */}
              {(() => {
                try {
                  const authInfo = JSON.parse(localStorage.getItem('authInfo'));
                  const userType = authInfo?.principalInfo?.userType;
                  return userType === 'CUSTOMER_ORG';
                } catch {
                  return false;
                }
              })() && accountData.creditLimit && (
                <>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}>Credit Limit</Typography>
                    <Typography variant="body2" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif", fontWeight: 500, color: '#673ab7' }}>{formatCurrency(accountData.creditLimit)}</Typography>
                  </Box>

                  {/* Show Available Credit for customers */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}>Available Credit</Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "'Roboto', 'Arial', sans-serif",
                        fontWeight: 500,
                        color: (accountData.totalInvoiced - accountData.totalPaid) > accountData.creditLimit ? 'error.main' : 'success.main'
                      }}
                    >
                      {formatCurrency(accountData.creditLimit - (accountData.totalInvoiced - accountData.totalPaid))}
                    </Typography>
                    {(accountData.totalInvoiced - accountData.totalPaid) > accountData.creditLimit && (
                      <Chip
                        label="Credit Limit Exceeded"
                        color="error"
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Financial Overview */}
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 1.5, 
          fontFamily: "'Roboto', 'Arial', sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500 
        }}
      >
        Financial Overview
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ boxShadow: 1, borderRadius: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <AccountBalanceWalletIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography 
                  variant="subtitle2"
                  sx={{ 
                    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Wallet Balance
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontFamily: "'Roboto', 'Arial', sans-serif",
                  fontSize: '0.9rem',
                  color: '#1976d2'
                }}
              >
                {formatCurrency(accountData.walletBalance)}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontFamily: "'Roboto', 'Arial', sans-serif" }}
              >
                Available for transactions
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button 
                  component={RouterLink} 
                  to="/account/wallet" 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    fontSize: '0.75rem', 
                    py: 0.5, 
                    fontFamily: "'Roboto', 'Arial', sans-serif",
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      borderColor: '#0d47a1',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  View Wallet
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 1, borderRadius: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <ReceiptIcon color="info" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography 
                  variant="subtitle2"
                  sx={{ 
                    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Total Invoiced
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontFamily: "'Roboto', 'Arial', sans-serif",
                  fontSize: '0.9rem',
                  color: '#0288d1'
                }}
              >
                {formatCurrency(accountData.totalInvoiced)}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif" }}
              >
                All time
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button
                  component={RouterLink}
                  to="/account/statements"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.5,
                    fontFamily: "'Roboto', 'Arial', sans-serif",
                    color: '#0288d1',
                    borderColor: '#0288d1',
                    '&:hover': {
                      borderColor: '#01579b',
                      backgroundColor: 'rgba(2, 136, 209, 0.04)'
                    }
                  }}
                >
                  View Invoices
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 1, borderRadius: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <PaymentIcon color="success" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Typography 
                  variant="subtitle2"
                  sx={{ 
                    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Total Paid
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  fontFamily: "'Roboto', 'Arial', sans-serif",
                  fontSize: '0.9rem',
                  color: '#2e7d32'
                }}
              >
                {formatCurrency(accountData.totalPaid)}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif" }}
              >
                All time
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Button
                  component={RouterLink}
                  to="/account/wallet?tab=3"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.5,
                    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                    color: '#2e7d32',
                    borderColor: '#2e7d32',
                    '&:hover': {
                      borderColor: '#1b5e20',
                      backgroundColor: 'rgba(46, 125, 50, 0.04)'
                    }
                  }}
                >
                  View Payments
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions */}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to="/account/wallet?tab=0"
          sx={{
            fontFamily: "'Roboto', 'Arial', sans-serif",
            textTransform: 'none'
          }}
        >
          View Transactions
        </Button>
        <Button
          variant="outlined"
          color="primary"
          component={RouterLink}
          to="/account/wallet?tab=1"
          sx={{
            fontFamily: "'Roboto', 'Arial', sans-serif",
            textTransform: 'none'
          }}
        >
          Top Up Wallet
        </Button>
      </Stack>
    </Box>
  );
};

export default AccountSummary;
